import { useCallback, useEffect, useState } from 'react';
import { useAuth } from './useAuth';
import {
  EMPTY_DRAFT,
  draftFromAppProfile,
  isDraftComplete,
  type AppProfileDraftRow,
  type ProfileDraft,
} from './types';
import { isQaInternalEmail, qaInternalDraft } from './qaInternal';
import { getSupabase } from '@/lib/supabase';
import { uploadToStorage } from './photoUpload';

/**
 * Profile-builder state — per-user localStorage-persisted draft.
 *
 * Storage key: `hottr.profileDraft.<userId>` so different mock accounts
 * don't share drafts. Returns the empty draft when no user is logged in,
 * so consumers can still render the builder before signup completes.
 *
 * Supabase swap path:
 *   - Replace `read()` / `write()` with `from('app_profiles').select/upsert`
 *   - `save()` already exists as the "flush to backend" entry point
 *   - Component API stays identical: `draft`, `isComplete`, `update`,
 *     `markCompleted`, `reset`, `save`
 */

const DRAFT_EVENT = 'hottr:profileDraft';

const keyFor = (userId: string | null | undefined) =>
  userId ? `hottr.profileDraft.${userId}` : 'hottr.profileDraft.anon';

const read = (userId: string | null | undefined): ProfileDraft => {
  if (typeof window === 'undefined') return EMPTY_DRAFT;
  try {
    const raw = window.localStorage.getItem(keyFor(userId));
    if (!raw) return EMPTY_DRAFT;
    const parsed = JSON.parse(raw);
    return { ...EMPTY_DRAFT, ...parsed } as ProfileDraft;
  } catch {
    return EMPTY_DRAFT;
  }
};

const write = (userId: string | null | undefined, draft: ProfileDraft) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(keyFor(userId), JSON.stringify(draft));
  window.dispatchEvent(new Event(DRAFT_EVENT));
};

export interface ProfileBuilderState {
  draft: ProfileDraft;
  isComplete: boolean;
  update(partial: Partial<ProfileDraft>): void;
  reset(): void;
  markCompleted(): void;
  /**
   * Future: flush draft to the canonical backend. In mock mode this is a
   * no-op past the localStorage write that `update`/`markCompleted` already
   * do. Returns `{ ok }` so the UI can show a success/error toast.
   */
  save(): Promise<{ ok: boolean; error?: string }>;
}

const PROFILE_DRAFT_SELECT =
  'handle, display_name, looking_for_tonight, wants_to_see, room, photo_url, additional_photos, interests';

export const useProfileBuilder = (): ProfileBuilderState => {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const qa = isQaInternalEmail(user?.email);

  const [draft, setDraft] = useState<ProfileDraft>(() => {
    if (qa) {
      const cached = read(userId);
      return cached.completedAt?.trim() ? cached : qaInternalDraft();
    }
    return read(userId);
  });

  useEffect(() => {
    const refresh = () => {
      if (isQaInternalEmail(user?.email)) {
        const cached = read(userId);
        setDraft(cached.completedAt?.trim() ? cached : qaInternalDraft());
        return;
      }
      setDraft(read(userId));
    };
    window.addEventListener('storage', refresh);
    window.addEventListener(DRAFT_EVENT, refresh);
    refresh();

    // Returning users: hydrate a real draft from app_profiles, not just completedAt.
    if (userId && !isQaInternalEmail(user?.email)) {
      const cached = read(userId);
      if (!cached.completedAt) {
        const supabase = getSupabase();
        if (supabase) {
          supabase
            .from('app_profiles')
            .select(PROFILE_DRAFT_SELECT)
            .eq('owner_id', userId)
            .maybeSingle()
            .then(({ data }) => {
              if (!data) return;
              write(userId, { ...read(userId), ...draftFromAppProfile(data as AppProfileDraftRow) });
            });
        }
      }
    }

    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener(DRAFT_EVENT, refresh);
    };
  }, [userId, user?.email]);

  const update = useCallback(
    (partial: Partial<ProfileDraft>) => {
      const next = { ...read(userId), ...partial };
      write(userId, next);
    },
    [userId],
  );

  const reset = useCallback(() => {
    write(userId, EMPTY_DRAFT);
  }, [userId]);

  const markCompleted = useCallback(() => {
    const current = read(userId);
    write(userId, { ...current, completedAt: new Date().toISOString() });
  }, [userId]);

  const save = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    const current = read(userId);

    const supabase = getSupabase();
    if (!supabase) {
      return { ok: true }; // mock mode — localStorage write already happened in update()
    }

    if (!userId) return { ok: false, error: 'Not logged in.' };

    // Upload any base64 photos to Storage; already-stored paths/URLs pass through.
    // We persist storage object paths (not signed URLs) — useProfiles mints
    // short-lived signed URLs at render time so we don't leak photos publicly.
    const photoPaths: string[] = [];
    for (let i = 0; i < current.photos.length; i++) {
      const result = await uploadToStorage(supabase, userId, current.photos[i], i);
      if (!result.ok) return { ok: false, error: result.error ?? 'Photo upload failed.' };
      if (result.path) photoPaths.push(result.path);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const db = supabase as any;
    const { error } = await db.from('app_profiles').upsert(
      {
        owner_id: userId,
        handle: current.handle,
        display_name: current.displayName,
        mood: current.mood || null,
        age: current.age,
        room: current.location,
        looking_for_tonight: current.lookingForTonight,
        wants_to_see: current.wantsToSee,
        height: current.height || null,
        weight: current.weight || null,
        body: current.body,
        position: current.position,
        cock_size: current.cockSize,
        interests: current.interests.filter((i) => i.trim().length > 0),
        tribe: current.tribe,
        photo_url: photoPaths[0] ?? null,
        additional_photos: photoPaths.slice(1),
        presence: 'live',
        last_active: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'owner_id' },
    ) as { error: { message: string } | null };

    if (error) return { ok: false, error: error.message };

    // Replace base64 photos in localStorage with the stored paths so a
    // subsequent save() is idempotent (no re-upload).
    if (photoPaths.length > 0) {
      write(userId, { ...current, photos: photoPaths });
    }

    return { ok: true };
  }, [userId]);

  const resolvedDraft = qa && !draft.completedAt?.trim() ? qaInternalDraft() : draft;

  return {
    draft: resolvedDraft,
    isComplete: qa || isDraftComplete(resolvedDraft),
    update,
    reset,
    markCompleted,
    save,
  };
};

export { isDraftComplete } from './types';
